import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createReviewedOutput,
  deleteReviewedOutput,
  fetchPolicy,
  fetchStylusJudgement,
} from '../services/api';
import {
  type AiJudgementSource,
  type Criteria,
  type DisagreementCandidateReview,
  type LabelType,
  type Policy,
  type ReviewedClassification,
  type StylusJudgement,
} from '../types';

export interface FallbackCandidateContext {
  transcriptionId: string;
  fragment: string;
  index: number;
  judgement: StylusJudgement | null;
  paperNumber?: number;
}

export interface DisagreementSessionCandidateAccessors<TCandidate> {
  getTranscriptionId: (c: TCandidate) => string;
  getEntityText: (c: TCandidate) => string;
  getPaperNumber: (c: TCandidate) => number;
  getDisplayLabel: (c: TCandidate) => LabelType;
  getAlreadyReviewed: (c: TCandidate) => boolean;
  getReview: (c: TCandidate) => DisagreementCandidateReview | null;
  getOriginalForSubmit: (c: TCandidate) => ReviewedClassification;
  applyOptimisticReview: (c: TCandidate, review: DisagreementCandidateReview) => TCandidate;
  getHasDisagreement?: (c: TCandidate) => boolean;
  createFallbackCandidate?: (ctx: FallbackCandidateContext) => TCandidate | null;
}

export interface UseDisagreementReviewSessionOpts<TCandidate>
  extends DisagreementSessionCandidateAccessors<TCandidate> {
  selectedCriteriaId: string;
  criteriaList: Criteria[];
  enabled: boolean;
  fetchCandidates: (criteriaId: string) => Promise<TCandidate[]>;
  judgementSource?: AiJudgementSource;
  /** When a criterion is selected, prefer this transcription if it is still a candidate. */
  requestedTranscriptionId?: string;
  onCandidatesError: () => void;
  onSubmitError: () => void;
  onSubmitSuccess?: (info: { wasUpdate: boolean }) => void;
  onUnresolvedCountChange?: (info: { criteriaId: string; delta: number }) => void;
  /** When true, includes transcriptions even if they have 0 disagreements (all agreed). */
  includeAllTranscriptions?: boolean;
}

export interface TranscriptionOption {
  value: string;
  label: string;
  unresolved: number;
}

export interface UseDisagreementReviewSessionResult<TCandidate> {
  loadingCandidates: boolean;
  selectedTranscriptionId: string;
  setSelectedTranscriptionId: (id: string) => void;
  judgement: StylusJudgement | null;
  loadingJudgement: boolean;
  selectedFragmentIndex: number | null;
  activePolicy: Policy | null;
  loadingPolicy: boolean;
  editType: ReviewedClassification | null;
  setEditType: (t: ReviewedClassification | null) => void;
  editReason: string;
  setEditReason: (r: string) => void;
  submitting: boolean;
  transcriptionOptions: TranscriptionOption[];
  fragments: string[];
  /** Legacy alias for consumers migrating from sentence-only judgements. */
  sentences: string[];
  selectedCandidate: TCandidate | null;
  unresolvedTotal: number;
  requestedTranscriptionMissing: boolean;
  handleFragmentClick: (fragment: string, index: number) => void;
  getLabelTypeForFragment: (index: number) => LabelType | null;
  isFragmentReviewed: (index: number) => boolean;
  handleSubmit: () => Promise<void>;
  goToPreviousTranscription: () => void;
  goToNextTranscription: () => void;
  goToPreviousLabel: () => void;
  goToNextLabel: () => void;
  disagreementLabelPosition: number | null;
  disagreementLabelCount: number;
}

const scrollFragmentToContainerTop = (index: number) => {
  const fragment = document.querySelector(`[data-testid="fragment-${index}"]`);
  if (!(fragment instanceof HTMLElement)) return;
  const container = fragment.closest('[data-testid="transcript-scroll"]');
  if (!(container instanceof HTMLElement)) {
    fragment.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }
  const top =
    fragment.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop;
  if (typeof container.scrollTo === 'function') {
    container.scrollTo({ top, behavior: 'smooth' });
  } else {
    container.scrollTop = top;
  }
};

export function useDisagreementReviewSession<TCandidate>(
  opts: UseDisagreementReviewSessionOpts<TCandidate>
): UseDisagreementReviewSessionResult<TCandidate> {
  const {
    selectedCriteriaId,
    criteriaList,
    enabled,
    fetchCandidates,
    judgementSource,
    requestedTranscriptionId = '',
    includeAllTranscriptions = false,
  } = opts;

  const [candidates, setCandidates] = useState<TCandidate[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedTranscriptionId, setSelectedTranscriptionId] = useState('');
  const [judgement, setJudgement] = useState<StylusJudgement | null>(null);
  const [loadingJudgement, setLoadingJudgement] = useState(false);
  const [selectedFragmentIndex, setSelectedFragmentIndex] = useState<number | null>(null);
  const [activePolicy, setActivePolicy] = useState<Policy | null>(null);
  const [loadingPolicy, setLoadingPolicy] = useState(false);
  const [editType, setEditType] = useState<ReviewedClassification | null>(null);
  const [editReason, setEditReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [requestedTranscriptionMissing, setRequestedTranscriptionMissing] = useState(false);

  const policyRequestIdRef = useRef(0);
  const candidatesRequestIdRef = useRef(0);
  const judgementRequestIdRef = useRef(0);
  const prevCriteriaIdRef = useRef('');
  const transcriptionCriteriaRef = useRef('');
  const selectedCriteriaIdRef = useRef(selectedCriteriaId);
  selectedCriteriaIdRef.current = selectedCriteriaId;
  const selectedTranscriptionIdRef = useRef('');
  const selectedFragmentIndexRef = useRef<number | null>(null);
  selectedFragmentIndexRef.current = selectedFragmentIndex;
  const pendingFragmentEdgeRef = useRef<'first' | 'last'>('first');
  const requestedTranscriptionIdRef = useRef(requestedTranscriptionId);
  requestedTranscriptionIdRef.current = requestedTranscriptionId;

  const updateSelectedTranscriptionId = useCallback((id: string) => {
    transcriptionCriteriaRef.current = id ? selectedCriteriaIdRef.current : '';
    selectedTranscriptionIdRef.current = id;
    setSelectedTranscriptionId(id);
    if (id) setRequestedTranscriptionMissing(false);
  }, []);

  const optsRef = useRef(opts);
  optsRef.current = opts;

  const getTranscriptionId = useCallback(
    (c: TCandidate) => optsRef.current.getTranscriptionId(c),
    []
  );
  const getEntityText = useCallback((c: TCandidate) => optsRef.current.getEntityText(c), []);
  const getPaperNumber = useCallback((c: TCandidate) => optsRef.current.getPaperNumber(c), []);
  const getDisplayLabel = useCallback((c: TCandidate) => optsRef.current.getDisplayLabel(c), []);
  const getAlreadyReviewed = useCallback(
    (c: TCandidate) => optsRef.current.getAlreadyReviewed(c),
    []
  );
  const getReview = useCallback((c: TCandidate) => optsRef.current.getReview(c), []);
  const getOriginalForSubmit = useCallback(
    (c: TCandidate) => optsRef.current.getOriginalForSubmit(c),
    []
  );
  const applyOptimisticReview = useCallback(
    (c: TCandidate, review: DisagreementCandidateReview) =>
      optsRef.current.applyOptimisticReview(c, review),
    []
  );
  const getHasDisagreement = useCallback((c: TCandidate) => {
    if (optsRef.current.getHasDisagreement) {
      return optsRef.current.getHasDisagreement(c);
    }
    const raw = (c as unknown as { hasDisagreement?: boolean }).hasDisagreement;
    return raw !== undefined ? raw : true;
  }, []);
  const createFallbackCandidate = useCallback((ctx: FallbackCandidateContext) => {
    if (optsRef.current.createFallbackCandidate) {
      return optsRef.current.createFallbackCandidate(ctx);
    }
    return null;
  }, []);

  const loadPolicy = useCallback((policyId: string) => {
    const requestId = ++policyRequestIdRef.current;
    setLoadingPolicy(true);
    fetchPolicy(policyId)
      .then(policy => {
        if (policyRequestIdRef.current === requestId) {
          setActivePolicy(policy);
          setLoadingPolicy(false);
        }
      })
      .catch(() => {
        if (policyRequestIdRef.current === requestId) {
          setActivePolicy(null);
          setLoadingPolicy(false);
        }
      });
  }, []);

  const reloadCandidates = useCallback(async (): Promise<TCandidate[]> => {
    if (!selectedCriteriaId) return [];
    const requestId = ++candidatesRequestIdRef.current;
    setLoadingCandidates(true);
    try {
      const data = await fetchCandidates(selectedCriteriaId);
      if (candidatesRequestIdRef.current !== requestId) return [];
      setCandidates(data);
      setLoadingCandidates(false);
      return data;
    } catch {
      if (candidatesRequestIdRef.current !== requestId) return [];
      setCandidates([]);
      setLoadingCandidates(false);
      optsRef.current.onCandidatesError();
      return [];
    }
  }, [selectedCriteriaId, fetchCandidates]);

  useEffect(() => {
    if (!enabled || !selectedCriteriaId) {
      policyRequestIdRef.current++;
      setActivePolicy(null);
      setLoadingPolicy(false);
      return;
    }
    const criteria = criteriaList.find(c => c.id === selectedCriteriaId);
    if (criteria?.policyId) {
      loadPolicy(criteria.policyId);
    } else if (criteriaList.length > 0) {
      policyRequestIdRef.current++;
      setActivePolicy(null);
      setLoadingPolicy(false);
    }
  }, [selectedCriteriaId, enabled, criteriaList, loadPolicy]);

  useEffect(() => {
    const criterionChanged = prevCriteriaIdRef.current !== selectedCriteriaId;
    if (criterionChanged) {
      updateSelectedTranscriptionId('');
      setJudgement(null);
      setSelectedFragmentIndex(null);
      setRequestedTranscriptionMissing(false);
    }

    if (!enabled || !selectedCriteriaId) {
      setCandidates([]);
      if (!selectedCriteriaId) {
        prevCriteriaIdRef.current = '';
        setRequestedTranscriptionMissing(false);
      }
      return;
    }

    prevCriteriaIdRef.current = selectedCriteriaId;

    reloadCandidates().then(data => {
      if (!criterionChanged && selectedTranscriptionIdRef.current) return;
      const requested = requestedTranscriptionIdRef.current;
      if (requested) {
        const hasRequested = data.some(c => getTranscriptionId(c) === requested);
        if (hasRequested) {
          updateSelectedTranscriptionId(requested);
        } else {
          setRequestedTranscriptionMissing(true);
        }
        return;
      }
      const firstUnresolved = [...data]
        .filter(c => getHasDisagreement(c))
        .sort((a, b) => Number(getAlreadyReviewed(a)) - Number(getAlreadyReviewed(b)))
        .find(c => getTranscriptionId(c));
      if (firstUnresolved) {
        updateSelectedTranscriptionId(getTranscriptionId(firstUnresolved));
      } else {
        const fallback = data.find(c => getTranscriptionId(c));
        if (fallback) updateSelectedTranscriptionId(getTranscriptionId(fallback));
      }
    });
    // accessor callbacks (getAlreadyReviewed, getTranscriptionId) are ref-stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCriteriaId, enabled, reloadCandidates, updateSelectedTranscriptionId]);

  useEffect(() => {
    if (!selectedCriteriaId || !selectedTranscriptionId) {
      judgementRequestIdRef.current++;
      setJudgement(null);
      setSelectedFragmentIndex(null);
      return;
    }
    if (transcriptionCriteriaRef.current !== selectedCriteriaId) {
      judgementRequestIdRef.current++;
      setJudgement(null);
      setSelectedFragmentIndex(null);
      return;
    }
    const requestId = ++judgementRequestIdRef.current;
    setLoadingJudgement(true);
    setSelectedFragmentIndex(null);
    fetchStylusJudgement(selectedCriteriaId, selectedTranscriptionId, judgementSource)
      .then(data => {
        if (judgementRequestIdRef.current !== requestId) return;
        setJudgement(data);
        setLoadingJudgement(false);
      })
      .catch(() => {
        if (judgementRequestIdRef.current !== requestId) return;
        setJudgement(null);
        setLoadingJudgement(false);
      });
  }, [selectedCriteriaId, selectedTranscriptionId, judgementSource]);

  const candidatesByTranscription = useMemo(() => {
    const map: Record<string, TCandidate[]> = {};
    for (const c of candidates) {
      const tid = getTranscriptionId(c);
      (map[tid] ||= []).push(c);
    }
    return map;
    // getTranscriptionId is ref-stable; omit from deps to avoid spurious recompute
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates]);

  const transcriptionOptions = useMemo<TranscriptionOption[]>(() => {
    return Object.entries(candidatesByTranscription)
      .map(([tid, list]) => {
        const paperNumber = list[0] !== undefined ? getPaperNumber(list[0]) : undefined;
        const disagreements = list.filter(c => getHasDisagreement(c));
        const unresolved = disagreements.filter(c => !getAlreadyReviewed(c)).length;
        const total = disagreements.length;
        const label = includeAllTranscriptions
          ? total > 0
            ? `Paper ${paperNumber} — ${unresolved}/${total} unresolved (${list.length} cases)`
            : `Paper ${paperNumber} — all agreed (${list.length} cases)`
          : `Paper ${paperNumber} — ${unresolved}/${total} unresolved`;
        return {
          value: tid,
          label,
          unresolved,
          total,
          hasDisagreements: total > 0,
        };
      })
      .filter(
        option =>
          includeAllTranscriptions ||
          option.hasDisagreements ||
          option.value === requestedTranscriptionIdRef.current ||
          option.value === selectedTranscriptionIdRef.current
      )
      .sort((a, b) => b.unresolved - a.unresolved)
      .map(({ value, label, unresolved }) => ({ value, label, unresolved }));
    // getPaperNumber, getAlreadyReviewed, getHasDisagreement are ref-stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatesByTranscription, getHasDisagreement, includeAllTranscriptions]);

  const candidateByEntity = useMemo(() => {
    const list = selectedTranscriptionId
      ? candidatesByTranscription[selectedTranscriptionId] || []
      : [];
    const byText: Record<string, TCandidate> = {};
    const byIndex: Record<number, TCandidate> = {};
    for (const c of list) {
      byText[getEntityText(c)] = c;
      const fIdx = (c as any).fragmentIndex;
      if (typeof fIdx === 'number') {
        byIndex[fIdx] = c;
      }
    }
    return { byText, byIndex };
    // getEntityText is ref-stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatesByTranscription, selectedTranscriptionId]);

  const fragments = useMemo(() => {
    if (!judgement) return [];
    const entities = judgement.entities ?? [];
    if (entities.length > 0) {
      return entities.map(entity => entity.text || entity.sentence);
    }
    return (judgement.sentences ?? []).map(sentence => sentence.sentence);
  }, [judgement]);

  const selectedCandidate = useMemo(() => {
    if (selectedFragmentIndex === null) return null;
    const fragment = fragments[selectedFragmentIndex];
    if (!fragment) return null;
    const fromIndex = candidateByEntity.byIndex[selectedFragmentIndex];
    if (fromIndex) return fromIndex;
    if (candidateByEntity.byText[fragment]) {
      return candidateByEntity.byText[fragment];
    }
    const activeOption = transcriptionOptions.find(o => o.value === selectedTranscriptionId);
    const paperNumber = activeOption
      ? parseInt(activeOption.label.match(/Paper (\d+)/)?.[1] || '0', 10)
      : undefined;
    return createFallbackCandidate({
      transcriptionId: selectedTranscriptionId,
      fragment,
      index: selectedFragmentIndex,
      judgement,
      paperNumber,
    });
  }, [
    selectedFragmentIndex,
    fragments,
    candidateByEntity,
    transcriptionOptions,
    selectedTranscriptionId,
    createFallbackCandidate,
    judgement,
  ]);

  const disagreementFragmentIndices = useMemo(
    () =>
      fragments
        .map((fragment, index) => {
          const candidate = candidateByEntity.byIndex[index] ?? candidateByEntity.byText[fragment];
          return candidate && getHasDisagreement(candidate) ? index : -1;
        })
        .filter(index => index >= 0),
    [fragments, candidateByEntity, getHasDisagreement]
  );
  const disagreementLabelCount = disagreementFragmentIndices.length;
  const disagreementLabelPosition = (() => {
    if (selectedFragmentIndex === null) return null;
    const position = disagreementFragmentIndices.indexOf(selectedFragmentIndex);
    return position >= 0 ? position + 1 : null;
  })();

  const scrollToFragment = useCallback((index: number) => {
    window.requestAnimationFrame(() => {
      scrollFragmentToContainerTop(index);
    });
  }, []);

  useEffect(() => {
    if (!selectedCandidate) {
      setEditType(null);
      setEditReason('');
      return;
    }
    const review = getReview(selectedCandidate);
    if (getAlreadyReviewed(selectedCandidate) && review) {
      setEditType(review.correctedClassification);
      setEditReason(review.reason);
    } else {
      setEditType(null);
      setEditReason('');
    }
  }, [selectedCandidate, getReview, getAlreadyReviewed]);

  useEffect(() => {
    if (!judgement) return;
    const disagreementIndices = fragments
      .map((fragment, index) => {
        const candidate = candidateByEntity.byIndex[index] ?? candidateByEntity.byText[fragment];
        return candidate && getHasDisagreement(candidate) ? index : -1;
      })
      .filter(index => index >= 0);
    if (disagreementIndices.length === 0) {
      if (
        includeAllTranscriptions &&
        fragments.length > 0 &&
        selectedFragmentIndexRef.current === null
      ) {
        setSelectedFragmentIndex(0);
        const id = window.requestAnimationFrame(() => {
          scrollFragmentToContainerTop(0);
        });
        return () => window.cancelAnimationFrame(id);
      }
      return;
    }
    const edge = pendingFragmentEdgeRef.current;
    pendingFragmentEdgeRef.current = 'first';
    const chosenIndex =
      edge === 'last'
        ? disagreementIndices[disagreementIndices.length - 1]
        : disagreementIndices[0];
    if (chosenIndex === undefined) return;
    setSelectedFragmentIndex(chosenIndex);
    const id = window.requestAnimationFrame(() => {
      scrollFragmentToContainerTop(chosenIndex);
    });
    return () => window.cancelAnimationFrame(id);
    // fragments and candidateByEntity are recomputed from judgement; scrolling
    // is gated on judgement-change, not on every consensus-toggle / submit reload
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [judgement, getHasDisagreement]);

  const getLabelTypeForFragment = useCallback(
    (index: number): LabelType | null => {
      const entityText = fragments[index];
      const candidate = candidateByEntity.byIndex[index] ?? candidateByEntity.byText[entityText];
      if (!candidate) return null;
      if (!getHasDisagreement(candidate)) {
        const display = getDisplayLabel(candidate);
        return display === 'no attempt' ? null : display;
      }
      const review = getReview(candidate);
      if (getAlreadyReviewed(candidate) && review) {
        return review.correctedClassification;
      }
      return getDisplayLabel(candidate);
    },
    [
      fragments,
      candidateByEntity,
      getHasDisagreement,
      getReview,
      getAlreadyReviewed,
      getDisplayLabel,
    ]
  );

  const isFragmentReviewed = useCallback(
    (index: number): boolean => {
      const entityText = fragments[index];
      const candidate = candidateByEntity.byIndex[index] ?? candidateByEntity.byText[entityText];
      return Boolean(candidate && getHasDisagreement(candidate) && getAlreadyReviewed(candidate));
    },
    [fragments, candidateByEntity, getHasDisagreement, getAlreadyReviewed]
  );

  const handleFragmentClick = useCallback((_fragment: string, index: number) => {
    setSelectedFragmentIndex(prev => (prev === index ? null : index));
  }, []);

  const goToAdjacentTranscription = useCallback(
    (direction: -1 | 1) => {
      if (transcriptionOptions.length === 0) return;
      const currentIndex = transcriptionOptions.findIndex(
        option => option.value === selectedTranscriptionIdRef.current
      );
      const start = currentIndex < 0 ? (direction === 1 ? -1 : 0) : currentIndex;
      const nextIndex =
        (start + direction + transcriptionOptions.length) % transcriptionOptions.length;
      const next = transcriptionOptions[nextIndex];
      if (next) updateSelectedTranscriptionId(next.value);
    },
    [transcriptionOptions, updateSelectedTranscriptionId]
  );

  const goToPreviousTranscription = useCallback(
    () => goToAdjacentTranscription(-1),
    [goToAdjacentTranscription]
  );
  const goToNextTranscription = useCallback(
    () => goToAdjacentTranscription(1),
    [goToAdjacentTranscription]
  );

  const goToAdjacentLabel = useCallback(
    (direction: -1 | 1) => {
      const current = selectedFragmentIndexRef.current;
      const targetIndices =
        includeAllTranscriptions && fragments.length > 0
          ? fragments.map((_, i) => i)
          : disagreementFragmentIndices;
      if (targetIndices.length === 0) return;
      const currentPos = current === null ? -1 : targetIndices.indexOf(current);
      const start = currentPos < 0 ? (direction === 1 ? -1 : 0) : currentPos;
      const nextPos = start + direction;
      if (nextPos >= 0 && nextPos < targetIndices.length) {
        const nextIndex = targetIndices[nextPos];
        if (nextIndex === undefined) return;
        setSelectedFragmentIndex(nextIndex);
        scrollToFragment(nextIndex);
        return;
      }

      if (transcriptionOptions.length <= 1) {
        if (targetIndices.length === 0) return;
        const wrappedPos = direction === 1 ? 0 : targetIndices.length - 1;
        const nextIndex = targetIndices[wrappedPos];
        if (nextIndex === undefined) return;
        setSelectedFragmentIndex(nextIndex);
        scrollToFragment(nextIndex);
        return;
      }

      pendingFragmentEdgeRef.current = direction === 1 ? 'first' : 'last';
      goToAdjacentTranscription(direction);
    },
    [
      disagreementFragmentIndices,
      fragments,
      goToAdjacentTranscription,
      includeAllTranscriptions,
      scrollToFragment,
      transcriptionOptions.length,
    ]
  );

  const goToPreviousLabel = useCallback(() => goToAdjacentLabel(-1), [goToAdjacentLabel]);
  const goToNextLabel = useCallback(() => goToAdjacentLabel(1), [goToAdjacentLabel]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPreviousLabel();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToNextLabel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPreviousLabel, goToNextLabel]);

  const handleSubmit = useCallback(async () => {
    if (!selectedCandidate || !activePolicy || !editType || !editReason.trim()) return;
    if (submitting) return;

    const candidate = selectedCandidate;
    const criteriaId = selectedCriteriaId;
    const previousReview = getReview(candidate);
    const wasAlreadyReviewed = getAlreadyReviewed(candidate);
    const targetTranscriptionId = getTranscriptionId(candidate);
    const targetEntityText = getEntityText(candidate);
    const reasonText = editReason.trim();

    const optimistic: DisagreementCandidateReview = {
      id: previousReview?.id ?? '__pending__',
      correctedClassification: editType,
      reason: reasonText,
      reviewedAt: new Date().toISOString(),
    };

    const targetFragmentIndex = (candidate as any).fragmentIndex ?? selectedFragmentIndex;

    const matches = (c: TCandidate) => {
      if (getTranscriptionId(c) !== targetTranscriptionId) return false;
      const cIdx = (c as any).fragmentIndex;
      if (typeof cIdx === 'number' && typeof targetFragmentIndex === 'number') {
        return cIdx === targetFragmentIndex;
      }
      return getEntityText(c) === targetEntityText;
    };

    setCandidates(prev => prev.map(c => (matches(c) ? applyOptimisticReview(c, optimistic) : c)));
    const currentPos =
      selectedFragmentIndex === null
        ? -1
        : disagreementFragmentIndices.indexOf(selectedFragmentIndex);
    const nextIndex =
      disagreementFragmentIndices.length === 0
        ? null
        : (disagreementFragmentIndices[
            (currentPos < 0 ? 0 : currentPos + 1) % disagreementFragmentIndices.length
          ] ?? null);
    setSelectedFragmentIndex(nextIndex);
    if (nextIndex !== null) scrollToFragment(nextIndex);
    if (!wasAlreadyReviewed) {
      optsRef.current.onUnresolvedCountChange?.({ criteriaId, delta: -1 });
    }
    optsRef.current.onSubmitSuccess?.({ wasUpdate: wasAlreadyReviewed });

    setSubmitting(true);
    try {
      if (wasAlreadyReviewed && previousReview) {
        await deleteReviewedOutput(previousReview.id);
      }
      const created = await createReviewedOutput({
        criteriaId,
        policyId: activePolicy.id,
        stylusTranscriptionId: targetTranscriptionId,
        sentence: targetEntityText,
        fragmentIndex: typeof targetFragmentIndex === 'number' ? targetFragmentIndex : undefined,
        originalClassification: getOriginalForSubmit(candidate),
        correctedClassification: editType,
        reason: reasonText,
        source: 'disagreement_review',
      });
      const confirmed: DisagreementCandidateReview = {
        id: created.id,
        correctedClassification: created.correctedClassification,
        reason: created.reason,
        reviewedAt: created.reviewedAt,
        fragmentIndex: created.fragmentIndex,
      };
      setCandidates(prev => prev.map(c => (matches(c) ? applyOptimisticReview(c, confirmed) : c)));
    } catch {
      setCandidates(prev => prev.map(c => (matches(c) ? candidate : c)));
      if (!wasAlreadyReviewed) {
        optsRef.current.onUnresolvedCountChange?.({ criteriaId, delta: 1 });
      }
      optsRef.current.onSubmitError();
    } finally {
      setSubmitting(false);
    }
  }, [
    selectedCandidate,
    activePolicy,
    editType,
    editReason,
    selectedCriteriaId,
    submitting,
    getReview,
    getAlreadyReviewed,
    getTranscriptionId,
    getEntityText,
    getOriginalForSubmit,
    applyOptimisticReview,
    selectedFragmentIndex,
    disagreementFragmentIndices,
    scrollToFragment,
  ]);

  const unresolvedTotal = candidates.filter(
    c => getHasDisagreement(c) && !getAlreadyReviewed(c)
  ).length;

  return {
    loadingCandidates,
    selectedTranscriptionId,
    setSelectedTranscriptionId: updateSelectedTranscriptionId,
    judgement,
    loadingJudgement,
    selectedFragmentIndex,
    activePolicy,
    loadingPolicy,
    editType,
    setEditType,
    editReason,
    setEditReason,
    submitting,
    transcriptionOptions,
    fragments,
    sentences: fragments,
    selectedCandidate,
    unresolvedTotal,
    requestedTranscriptionMissing,
    handleFragmentClick,
    getLabelTypeForFragment,
    isFragmentReviewed,
    handleSubmit,
    goToPreviousTranscription,
    goToNextTranscription,
    goToPreviousLabel,
    goToNextLabel,
    disagreementLabelPosition,
    disagreementLabelCount,
  };
}
